import { createClient } from '@/lib/supabase/client';

export type LoginAction = 'login_sukses' | 'login_error';
export type LoginRole = 'siswa' | 'guru' | 'admin';

export interface LoginLogPayload {
  role: LoginRole;
  username: string;
  action: LoginAction;
  error_msg?: string;
}

/**
 * Parse User-Agent string to extract browser and OS info.
 */
function parseUserAgent(ua: string): { browser: string; os: string } {
  let browser = 'Unknown';
  let os = 'Unknown';

  // OS detection
  if (/Windows NT 10/.test(ua)) os = 'Windows 10';
  else if (/Windows NT 6\.3/.test(ua)) os = 'Windows 8.1';
  else if (/Windows NT 6\.1/.test(ua)) os = 'Windows 7';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) {
    const m = ua.match(/Android ([0-9.]+)/);
    os = m ? `Android ${m[1]}` : 'Android';
  } else if (/iPhone|iPad|iPod/.test(ua)) {
    const m = ua.match(/OS ([0-9_]+)/);
    os = m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/Mac OS X/.test(ua)) {
    const m = ua.match(/Mac OS X ([0-9_]+)/);
    os = m ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  } else if (/Linux/.test(ua)) os = 'Linux';

  // Browser detection (order matters — check Edge/OPR before Chrome)
  if (/Edg\//.test(ua)) {
    const m = ua.match(/Edg\/([0-9.]+)/);
    browser = m ? `Edge ${m[1].split('.')[0]}` : 'Edge';
  } else if (/OPR\//.test(ua)) {
    const m = ua.match(/OPR\/([0-9.]+)/);
    browser = m ? `Opera ${m[1].split('.')[0]}` : 'Opera';
  } else if (/Firefox\//.test(ua)) {
    const m = ua.match(/Firefox\/([0-9.]+)/);
    browser = m ? `Firefox ${m[1].split('.')[0]}` : 'Firefox';
  } else if (/Chrome\//.test(ua)) {
    const m = ua.match(/Chrome\/([0-9.]+)/);
    browser = m ? `Chrome ${m[1].split('.')[0]}` : 'Chrome';
  } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
    const m = ua.match(/Version\/([0-9.]+)/);
    browser = m ? `Safari ${m[1].split('.')[0]}` : 'Safari';
  }

  return { browser, os };
}

/**
 * Record a login attempt to the login_logs table.
 * IP address is best-effort from client side (may be local/private).
 */
export async function recordLoginLog(payload: LoginLogPayload): Promise<void> {
  try {
    const supabase = createClient();

    let browser = 'Unknown';
    let os = 'Unknown';
    let ip_address = '';

    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent;
      const parsed = parseUserAgent(ua);
      browser = parsed.browser;
      os = parsed.os;
    }

    // Try to get public IP via a lightweight API (best-effort, no throw)
    try {
      const res = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
      if (res.ok) {
        const json = await res.json();
        ip_address = json.ip ?? '';
      }
    } catch {
      ip_address = '';
    }

    await supabase.from('login_logs').insert({
      role: payload.role,
      username: payload.username,
      ip_address: ip_address || null,
      browser,
      os,
      action: payload.action,
      error_msg: payload.error_msg ?? null,
    });
  } catch {
    // Never throw — logging must not block login flow
  }
}
